import {
	BrandPalette,
	LikedItem,
	Motivations,
	PositioningGenerationContext,
	PositioningLane,
	PositioningOutputSnapshot,
	UserPersona,
} from '../../../../src/commonTypes';
import { getPositioningOutputSnapshots } from '../../../../src/positioningOutputs';

type PersonaField =
	| 'ageGroup'
	| 'gender'
	| 'biography'
	| 'goals'
	| 'needs'
	| 'painPoints'
	| 'motivations'
	| 'influences';

export type SavedOutputCardData = PositioningOutputSnapshot & {
	item: LikedItem;
	itemId?: string;
	clientId?: string;
	ownedByCurrentUser: boolean;
	ownerName?: string;
	ownerColour?: string;
	likedByCurrentUser: boolean;
	likeCount: number;
	likedByUsers: NonNullable<LikedItem['likedByUsers']>;
};

export type PositioningCardData = {
	itemId?: string;
	clientId?: string;
	sourceOutputId: string;
	version: number;
	lane: PositioningLane;
	eyebrow: string;
	title: string;
	subtitle?: string;
	summary: string;
	imageUrl?: string;
	positioningContext?: PositioningGenerationContext;
	persona?: UserPersona;
	palette?: BrandPalette;
	editable?: boolean;
	ownedByCurrentUser: boolean;
	ownerName?: string;
	ownerColour?: string;
	rawText?: string;
	motivations?: Motivations;
	motivationIndex?: number;
	likedByCurrentUser: boolean;
	likeCount: number;
	likedByUsers: NonNullable<LikedItem['likedByUsers']>;
	details: Array<{ title: string; value: string; field?: PersonaField }>;
};

const ownershipFor = (item: LikedItem) => ({
	ownedByCurrentUser: item.isOwner !== false,
	ownerName: item.addedBy?.name,
	ownerColour: item.addedBy?.colour,
	likedByCurrentUser: Boolean(item.likedByCurrentUser),
	likeCount: item.likeCount || 0,
	likedByUsers: item.likedByUsers || [],
});

export const likedItemsToSavedOutputCards = (items: LikedItem[]): SavedOutputCardData[] =>
	items.flatMap((item) =>
		getPositioningOutputSnapshots(item).map((snapshot) => ({
			...snapshot,
			...ownershipFor(item),
			item,
			itemId: item._id,
			clientId: item.clientId,
		}))
	);

/** Removes Idea Box-only ownership and source-record fields before board persistence. */
export const toPositioningOutputSnapshot = (
	card: SavedOutputCardData
): PositioningOutputSnapshot => ({
	schemaVersion: card.schemaVersion,
	sourceOutputId: card.sourceOutputId,
	sourceLikeId: card.sourceLikeId,
	version: card.version,
	type: card.type,
	sourceType: card.sourceType,
	lane: card.lane,
	eyebrow: card.eyebrow,
	title: card.title,
	subtitle: card.subtitle,
	summary: card.summary,
	imageUrl: card.imageUrl,
	positioningContext: card.positioningContext,
	payload: structuredClone(card.payload),
	details: structuredClone(card.details),
});

const personaDetails = (persona: UserPersona): PositioningCardData['details'] => [
	{
		title: 'Age group',
		value: persona.ageGroup || '25–34',
		field: 'ageGroup',
	},
	...(persona.gender
		? [{ title: 'Gender', value: persona.gender, field: 'gender' as const }]
		: []),
	{ title: 'Who they are', value: persona.biography, field: 'biography' },
	{ title: 'Goals', value: persona.goals, field: 'goals' },
	{ title: 'Needs', value: persona.needs, field: 'needs' },
	{ title: 'Pain points', value: persona.painPoints, field: 'painPoints' },
	{ title: 'Motivations', value: persona.motivations, field: 'motivations' },
	{ title: 'Influences', value: persona.influences, field: 'influences' },
];

const toPositioningCard = (card: SavedOutputCardData): PositioningCardData | null => {
	if (!card.lane) return null;
	const result: PositioningCardData = {
		...ownershipFor(card.item),
		itemId: card.itemId,
		clientId: card.clientId,
		sourceOutputId: card.sourceOutputId,
		version: card.version,
		lane: card.lane,
		eyebrow: card.eyebrow,
		title: card.title,
		subtitle: card.subtitle,
		summary: card.summary,
		imageUrl: card.imageUrl,
		positioningContext: card.positioningContext,
		editable: card.item.isOwner !== false && Boolean(card.item._id),
		details: card.details,
	};

	if (card.payload.kind === 'persona') {
		result.persona = card.payload.persona;
		result.details = personaDetails(card.payload.persona);
	} else if (card.payload.kind === 'motivation') {
		result.motivations = card.payload.motivations;
		result.motivationIndex = card.payload.stakeholderIndex;
	} else if (card.payload.kind === 'palette') {
		result.palette = card.payload.palette;
	} else if (card.payload.kind === 'text') {
		result.rawText = card.payload.text;
	} else if (card.payload.kind === 'record') {
		result.rawText = card.summary;
	}
	return result;
};

export const likedItemsToPositioningCards = (items: LikedItem[]): PositioningCardData[] =>
	likedItemsToSavedOutputCards(items)
		.map(toPositioningCard)
		.filter((card): card is PositioningCardData => Boolean(card));
